import React, { ReactNode, useEffect, useState } from 'react';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { translate } from '../../../services/translate';
import CustomButton from '../../../components/CustomButton';
import Divider from '../../../components/Divider';
import { Tabs } from '../../../components/Tabs';
import FormInput from '../../../components/FormInput';
import { WindowInformation } from '../../../services/window-information';
import Tooltip from '../../../components/Tooltip';
import { faChevronDown, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import VacancyLevelsInputs from './VacancyLevelsInputs';
import ActingBodiesCommandsDropdown from '../../../components/Dropdowns/ActingBodiesCommandDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import WorkplacesDropdown from '../../../components/deac/WorkplacesDropdown';
import VacancyTagsInputs from './VacancyTagsInputs';
import VacancyRequirementsInputs from './VacancyRequirementsInputs';
import { useAuth } from '../../../context/Auth';
import moment from 'moment';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import { CalendarValue } from '../../../components/DateTimePicker';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { deacService } from '../../../services/central-api/deac';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import VacancyApplications from './VacancyApplications';
import { getVacancyStatus } from './VacanciesCommons';
import DeleteVacancyModal from './DeleteVacancyModal';
import UnitProgramDropdown from '../../../components/deac/UnitProgramDropdown';
import { getDeacSchedule } from '../DeacCommons';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import CancelApplicationsModal from './CloseApplicationsModal';
import ExportAgentsSchedulesSelectModal from './ExportAgentsSchedulesSelectModal';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { useQuery } from '@tanstack/react-query';
import { DeacParamList } from '../../../typings/Params';
import { RootNavigation } from '../../../Routes';

interface SectionHeaderParams {
    title: string;
    endContent?: ReactNode;
    children: ReactNode;
    viewZIndex?: number;
    childrenZIndex?: number;
    required?: boolean;
}

function SectionHeader({ children, endContent, title, viewZIndex, childrenZIndex, required }: SectionHeaderParams) {
    const { styles, theme } = useStyles(styleSheet);

    return <View style={{ flexDirection: 'column', rowGap: 16, zIndex: viewZIndex }}>
        <View style={{ flexDirection: 'column', rowGap: 8, zIndex: 2 }}>
            <View style={{ flexDirection: 'row', columnGap: 8, alignItems: 'center' }}>
                <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'baseline' }}>
                    <MyAppText style={styles.sectionHeaderTitle}>{translate(title)}</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                {endContent ? endContent : null}
            </View>
            <Divider style={{ zIndex: -1 }} color={theme.colors.primary['200']} />
        </View>
        <View style={{ zIndex: childrenZIndex ?? 1, gap: 4 }}>
            {children}
        </View>
    </View>;
}

export default function VacancyForm({ navigation }: {
    navigation: RootNavigation;
}) {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<DeacParamList, 'VacancyForm'>>();

    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const inViewMode = !(authenticatedUser?.permissions.deac_manage_vacancies ||
        authenticatedUser?.permissions.deac_manage_acting_body_units_vacancies ||
        authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies
    );

    const [isCancellingApplications, setIsCancellingApplications] = useState<boolean>(false);
    const [vacancy, setVacancy] = useState<Vacancy | null>(null);

    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [quantity, setQuantity] = useState<number>(0);
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(0);
    const [actingBodyCommandId, setActingBodyCommandId] = useState<number>(0);
    const [address, setAddress] = useState<string>('');
    const [workplaceId, setWorkplaceId] = useState<number>(0);
    const [unitProgramId, setUnitProgramId] = useState<number>(0);
    const [levelsIds, setLevelsIds] = useState<number[]>([]);
    const [requirementsIds, setRequirementsIds] = useState<number[]>([]);
    const [tagsIds, setTagsIds] = useState<number[]>([]);

    const [applicationsStartInDateFilter, setApplicationsStartInDateFilter] = useState<CalendarValue>(null);
    const [applicationsFinishInDateFilter, setApplicationsFinishInDateFilter] = useState<CalendarValue>(null);
    const [applicationsStartInHourFilter, setApplicationsStartInHourFilter] = useState('');
    const [applicationsFinishInHourFilter, setApplicationsFinishInHourFilter] = useState('');
    const [applicationsEstimatedStartZIndex, setApplicationsEstimatedStartZIndex] = useState<number>(2);
    const [applicationsEstimatedFinishZIndex, seApplicationsEstimatedFinishZIndex] = useState<number>(1);

    const [activityStartInDateFilter, setActivityStartInDateFilter] = useState<CalendarValue>(null);
    const [activityFinishInDateFilter, setActivityFinishInDateFilter] = useState<CalendarValue>(null);
    const [activityStartInHourFilter, setActivityStartInHourFilter] = useState('');
    const [activityFinishInHourFilter, setActivityFinishInHourFilter] = useState('');
    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingActions, setIsLoadingActions] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

    const [closeApplicationsConfirmModal, setCloseApplicationsConfirmModal] = useState<boolean>(false);
    const [exportScheduleModal, setExportScheduleModal] = useState<boolean>(false);
    const [scheduleFileFormat, setScheduleFileFormat] = useState<'pdf' | 'csv' | 'xlsx' | ''>('');

    const isScheduled = vacancy && (vacancy.deletedAt == null && moment().isBefore(moment(vacancy.applicationsStart)));
    const isOpened = vacancy && (vacancy.applicationsFinish
        ? (vacancy.deletedAt == null && moment().isAfter(moment(vacancy.applicationsStart)) && moment().isBefore(moment(vacancy.applicationsFinish)))
        : (vacancy.deletedAt == null && moment().isAfter(moment(vacancy.applicationsStart)) && moment().isBefore(moment(vacancy.activityStart))));
    const isCompleted = vacancy && (vacancy.applicationsFinish
        ? (vacancy.deletedAt == null && moment().isAfter(moment(vacancy.applicationsFinish)) && moment().isBefore(moment(vacancy.activityStart)))
        : false);
    const isProgress = vacancy && (vacancy.deletedAt == null && moment().isAfter(moment(vacancy.activityStart)) && moment().isBefore(moment(vacancy.activityFinish)));
    const isFinished = vacancy && (vacancy.deletedAt == null && moment().isAfter(moment(vacancy.activityFinish)));
    const isCanceled = vacancy && (vacancy.deletedAt != null);
    const IsEditing = route.params?.id ? true : false;
    const isReadingMode = inViewMode || ((IsEditing && (isProgress || isFinished || isCanceled)) ?? false);

    function cleanValues() {
        setTitle('');
        setDescription('');
        setQuantity(0);
        setActingBodyId(0);
        setActingBodyUnitId(0);
        setActingBodyCommandId(0);
        setAddress('');
        setWorkplaceId(0);
        setUnitProgramId(0);
        setLevelsIds([]);
        setRequirementsIds([]);
        setTagsIds([]);

        setApplicationsStartInDateFilter(null);
        setApplicationsStartInHourFilter('');
        setApplicationsFinishInDateFilter(null);
        setApplicationsFinishInHourFilter('');

        setActivityStartInDateFilter(null);
        setActivityStartInHourFilter('');
        setActivityFinishInDateFilter(null);
        setActivityFinishInHourFilter('');
    }

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacVacancyForm', route.params?.id],
        queryFn: () => deacService.getVacancy(route.params?.id || 0),
        enabled: !!authenticatedUser && isFocused && !!route.params && !!route.params.id,
    });

    const isEnabled = !!authenticatedUser && isFocused && !!route.params && !!route.params.id;
    const isFetchingContent = isEnabled && queryResult.isFetching;

    const { data } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }

        setVacancy(data);
        setTitle(data.title);
        setDescription(data.description);
        setQuantity(data.quantity);
        setActingBodyId(data.workplace.actingBodyUnit.actingBody.id);
        setActingBodyUnitId(data.workplace.actingBodyUnit.id);
        setActingBodyCommandId(data.workplace.actingBodyCommand.id);
        setAddress(data.workplace.address);
        setWorkplaceId(data.workplace.id);
        setUnitProgramId(data.unitProgram.id);
        setLevelsIds(data.levels.map(l => l.id));
        setRequirementsIds(data.skills.map(s => s.id));
        setTagsIds(data.tags?.map(t => t.id) ?? []);

        setApplicationsStartInDateFilter(new Date(data.applicationsStart));
        setApplicationsStartInHourFilter(moment(data.applicationsStart).format('HH:mm'));
        setApplicationsFinishInDateFilter(data.applicationsFinish ? new Date(data.applicationsFinish) : null);
        setApplicationsFinishInHourFilter(data.applicationsFinish ? moment(data.applicationsFinish).format('HH:mm') : '');

        setActivityStartInDateFilter(new Date(data.activityStart));
        setActivityStartInHourFilter(moment(data.activityStart).format('HH:mm'));
        setActivityFinishInDateFilter(new Date(data.activityFinish));
        setActivityFinishInHourFilter(moment(data.activityFinish).format('HH:mm'));
    }, [data]);

    async function formSubmit() {
        try {
            setIsLoading(true);
            const formatDateTime = (date: Date | null, time: string) =>
                date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').toDate() : undefined;

            const activityStart = moment(`${moment(activityStartInDateFilter).format('DD/MM/YYYY')} ${activityStartInHourFilter}`, 'DD/MM/YYYY HH:mm').toDate();
            const activityFinish = moment(`${moment(activityFinishInDateFilter).format('DD/MM/YYYY')} ${activityFinishInHourFilter}`, 'DD/MM/YYYY HH:mm').toDate();
            const applicationsStart = moment(`${moment(applicationsStartInDateFilter).format('DD/MM/YYYY')} ${applicationsStartInHourFilter}`, 'DD/MM/YYYY HH:mm').toDate();
            const applicationsFinish = formatDateTime(applicationsFinishInDateFilter, applicationsFinishInHourFilter ? `${applicationsFinishInHourFilter}:59` : '23:59:59');

            if (route.params?.id) {
                await deacService.updateVacancy({
                    id: route.params.id,
                    title: title,
                    description: description,
                    quantity: quantity,
                    levelsIds: levelsIds,
                    skillsIds: requirementsIds,
                    tagsIds: tagsIds.length > 0 ? tagsIds : undefined,
                    workplaceId: workplaceId,
                    unitProgramId: unitProgramId,
                    activityStart: activityStart,
                    activityFinish: activityFinish,
                    applicationsStart: applicationsStart,
                    applicationsFinish: applicationsFinish ? applicationsFinish : undefined
                });
            } else {
                await deacService.createVacancy({
                    title: title,
                    description: description,
                    quantity: quantity,
                    levelsIds: levelsIds,
                    skillsIds: requirementsIds,
                    tagsIds: tagsIds.length > 0 ? tagsIds : undefined,
                    workplaceId: workplaceId,
                    unitProgramId: unitProgramId,
                    activityStart: activityStart,
                    activityFinish: activityFinish,
                    applicationsStart: applicationsStart,
                    applicationsFinish: applicationsFinish ? applicationsFinish : undefined
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            cleanValues();
            navigation.navigate('Deac', { screen: 'Vacancies' });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    function isFormValid(): boolean {
        if (title == '' ||
            description == '' ||
            workplaceId == 0 ||
            unitProgramId == 0 ||
            quantity == 0 ||
            levelsIds.length == 0 ||
            requirementsIds.length == 0
        ) {
            return false;
        }

        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss') : undefined;

        const applicationsStart = moment(`${moment(applicationsStartInDateFilter).format('DD/MM/YYYY')} ${applicationsStartInHourFilter}`, 'DD/MM/YYYY HH:mm');
        const applicationsFinish = formatDateTime(applicationsFinishInDateFilter, applicationsFinishInHourFilter ? `${applicationsFinishInHourFilter}:59` : '23:59:59');
        const activityStart = moment(`${moment(activityStartInDateFilter).format('DD/MM/YYYY')} ${activityStartInHourFilter}`, 'DD/MM/YYYY HH:mm');
        const activityFinish = moment(`${moment(activityFinishInDateFilter).format('DD/MM/YYYY')} ${activityFinishInHourFilter}`, 'DD/MM/YYYY HH:mm');

        if (applicationsFinish) {
            return applicationsStart.isBefore(applicationsFinish) && applicationsFinish.isBefore(activityStart) && activityStart.isBefore(activityFinish);
        } else {
            return applicationsStart.isBefore(activityStart) && activityStart.isBefore(activityFinish);
        }
    }

    function applicationsFinishLaterThanStart() {
        if (applicationsStartInDateFilter && applicationsFinishInDateFilter) {
            const applicationsStart = moment(`${moment(applicationsStartInDateFilter).format('DD/MM/YYYY')} ${applicationsStartInHourFilter}`, 'DD/MM/YYYY HH:mm');
            const applicationsFinish = moment(`${moment(applicationsFinishInDateFilter).format('DD/MM/YYYY')} ${applicationsFinishInHourFilter}`, 'DD/MM/YYYY HH:mm');

            return applicationsStart.isBefore(applicationsFinish);
        }

        return true;
    }

    function activityStartLaterThanApplicationsFinish() {
        if (applicationsFinishInDateFilter && activityStartInDateFilter) {
            const applicationsFinish = moment(`${moment(applicationsFinishInDateFilter).format('DD/MM/YYYY')} ${applicationsFinishInHourFilter}`, 'DD/MM/YYYY HH:mm');
            const activityStart = moment(`${moment(activityStartInDateFilter).format('DD/MM/YYYY')} ${activityStartInHourFilter}`, 'DD/MM/YYYY HH:mm');

            return applicationsFinish.isBefore(activityStart);
        }

        return true;
    }

    function activityStartLaterThanApplicationsStart() {
        if (!applicationsFinishInDateFilter && applicationsStartInDateFilter && activityStartInDateFilter) {
            const applicationsStart = moment(`${moment(applicationsStartInDateFilter).format('DD/MM/YYYY')} ${applicationsStartInHourFilter}`, 'DD/MM/YYYY HH:mm');
            const activityStart = moment(`${moment(activityStartInDateFilter).format('DD/MM/YYYY')} ${activityStartInHourFilter}`, 'DD/MM/YYYY HH:mm');

            return applicationsStart.isBefore(activityStart);
        }

        return true;
    }

    function activityFinishLaterThanActivityStart() {
        if (activityStartInDateFilter && activityFinishInDateFilter) {
            const activityStart = moment(`${moment(activityStartInDateFilter).format('DD/MM/YYYY')} ${activityStartInHourFilter}`, 'DD/MM/YYYY HH:mm');
            const activityFinish = moment(`${moment(activityFinishInDateFilter).format('DD/MM/YYYY')} ${activityFinishInHourFilter}`, 'DD/MM/YYYY HH:mm');

            return activityStart.isBefore(activityFinish);
        }

        return true;
    }

    const getActingBodyId = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies) {
            return actingBodyId;
        }
        return authenticatedUser.actingBodyUnit.actingBodyId;
    };

    function getActingBodyUnitId() {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies ||
            authenticatedUser.permissions.deac_manage_acting_body_units_vacancies
        ) {
            return actingBodyUnitId;
        }
        return authenticatedUser.actingBodyUnitId;
    }

    async function getSchedule(mode: 'pdf' | 'csv' | 'xlsx') {
        if (!vacancy) {
            return;
        }

        getDeacSchedule(mode, setIsLoadingActions, {}, [vacancy.id]);
    }

    async function closeApplications() {
        try {
            if (!vacancy) {
                return;
            }

            setIsLoadingActions(true);
            const applicationsFinish = moment().toDate();
            await deacService.closeVacanciesApplications({
                vacanciesIds: [vacancy.id],
                applicationsFinish: applicationsFinish
            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setApplicationsFinishInDateFilter(applicationsFinish);
            setApplicationsFinishInHourFilter(moment(applicationsFinish).format('HH:mm'));
            setVacancy({
                ...vacancy,
                applicationsFinish: applicationsFinish
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoadingActions(false);
        }
    }

    function getActions() {
        const actions: Action[] = [];

        if (!isScheduled) {
            actions.push({
                content: translate('exportSchedule'),
                onPress: () => setExportScheduleModal(true)
            });
        }

        if (isOpened && !inViewMode) {
            actions.push({
                content: translate('closeApplications'),
                variant: 'danger',
                onPress: async () => setCloseApplicationsConfirmModal(true)
            });
        }

        if ((isScheduled || isOpened) && !inViewMode) {
            actions.push({
                content: translate('cancelVacancy'),
                variant: 'danger',
                onPress: () => setIsDeleteModalVisible(true)
            });
        }

        return actions;
    }

    function showSaveButton() {
        return (!IsEditing || isScheduled || isOpened || isCompleted) && !inViewMode && !isCancellingApplications;
    }

    return (
        <Container>
            <Header
                title={IsEditing ? translate('viewVacancy') : translate('registerVacancy')}
                goBackAction={() => {
                    cleanValues();
                    navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Deac', { screen: 'Vacancies' });
                }}
                actions={<View style={{ flexDirection: 'row', gap: 8 }}>
                    {(IsEditing && !isFinished && !isCanceled)
                        ? <DropdownButton
                            viewStyle={{ height: '100%' }}
                            isLoading={isLoadingActions}
                            isButton
                            actions={getActions()}
                            buttonIcon={faChevronDown}
                            buttonType={showSaveButton() ? 'secondary' : 'primary'}
                            buttonLabel='actions'
                            offsetX={showSaveButton() ? -49 : 37}
                            offsetY={45}
                            size={164}
                        />
                        : null}
                    {showSaveButton() ? <CustomButton text={translate('save')} onPress={formSubmit} disabled={!isFormValid()} isLoading={isLoading} /> : null}
                </View>
                }
            />
            <Card>
                {isFetchingContent
                    ? <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', }}>
                        <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                    </View>
                    :
                    <Tabs
                        tabs={[{
                            key: 'vacancyInfo',
                            label: translate('vacancyInfo'),
                        }, {
                            key: 'applications',
                            label: translate('applications'),
                        }]}
                        endContent={
                            IsEditing
                                ? vacancy && getVacancyStatus({ vacancy, style: { alignSelf: 'flex-end' } })
                                : null
                        }
                    >
                        {({ selectedTab }) => {
                            if (selectedTab == 'vacancyInfo') {
                                return <ScrollView contentContainerStyle={{ flexDirection: 'column', padding: 4, rowGap: 24 }}>
                                    <SectionHeader title='activityTitle'>
                                        <FormInput
                                            value={title}
                                            onChangeText={setTitle}
                                            required
                                            label={translate('activityTitle')}
                                            labelStyle={styles.fieldsLabelStyle}
                                            maxCharacters={50}
                                            viewStyle={[{ gap: 8 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                            disabled={isReadingMode}
                                        />
                                    </SectionHeader>

                                    <SectionHeader title='activityDescription'>
                                        <FormInput
                                            value={description}
                                            onChangeText={setDescription}
                                            required
                                            label={translate('description')}
                                            labelStyle={styles.fieldsLabelStyle}
                                            maxCharacters={140}
                                            numberOfLines={3}
                                            multiline
                                            viewStyle={[{ gap: 8 }]}
                                            disabled={isReadingMode}
                                        />
                                    </SectionHeader>

                                    <SectionHeader viewZIndex={8} title='correspondentWorkplace'>
                                        <View style={{ flexDirection: 'column', gap: 16 }}>
                                            <View style={{ zIndex: 2, flexDirection: windowInfo.isMobile ? 'column' : 'row', flex: 1, flexWrap: 'wrap', gap: 16 }}>
                                                <ActingBodiesDropdown
                                                    value={getActingBodyId()}
                                                    setValue={(value) => {
                                                        setActingBodyId(value);

                                                        setActingBodyUnitId(0);
                                                        setWorkplaceId(0);
                                                        setActingBodyCommandId(0);
                                                        setAddress('');
                                                    }}
                                                    labelStyle={styles.fieldsLabelStyle}
                                                    zIndex={5}
                                                    includeAllOption={false}
                                                    required
                                                    disabled={(isReadingMode || (IsEditing && !isScheduled) || !authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies)}
                                                    viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                                />
                                                <ActingBodiesUnitsDropdown
                                                    value={getActingBodyUnitId()}
                                                    setValue={(value) => {
                                                        setActingBodyUnitId(value);

                                                        setWorkplaceId(0);
                                                        setActingBodyCommandId(0);
                                                        setAddress('');
                                                    }}
                                                    actingBodyId={getActingBodyId()}
                                                    labelStyle={styles.fieldsLabelStyle}
                                                    zIndex={4}
                                                    required
                                                    disabled={(
                                                        isReadingMode ||
                                                        (IsEditing && !isScheduled) ||
                                                        (!authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies &&
                                                            !authenticatedUser?.permissions.deac_manage_acting_body_units_vacancies)
                                                    )}
                                                    viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                                />
                                                <WorkplacesDropdown
                                                    value={workplaceId}
                                                    setValue={setWorkplaceId}
                                                    zIndex={3}
                                                    disable={!getActingBodyId() || !getActingBodyUnitId() || ((isReadingMode || (IsEditing && !isScheduled)))}
                                                    actingBodyId={getActingBodyId()}
                                                    actingBodyUnitId={getActingBodyUnitId()}
                                                    setUnit={setActingBodyUnitId}
                                                    setCommand={setActingBodyCommandId}
                                                    setAddress={setAddress}
                                                    required
                                                    labelStyle={styles.fieldsLabelStyle}
                                                    viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                                />
                                                <ActingBodiesCommandsDropdown
                                                    actingBodyId={getActingBodyId()}
                                                    disabled={true}
                                                    value={actingBodyCommandId}
                                                    setValue={setActingBodyCommandId}
                                                    labelStyle={styles.fieldsLabelStyle}
                                                    zIndex={2}
                                                    viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                                />
                                            </View>
                                            <View style={{ zIndex: 1 }}>
                                                <FormInput
                                                    value={address}
                                                    onChangeText={setAddress}
                                                    label={translate('location')}
                                                    labelStyle={styles.fieldsLabelStyle}
                                                    disabled
                                                />
                                            </View>
                                        </View>
                                    </SectionHeader>

                                    <SectionHeader viewZIndex={7} title='linkedProgram'>
                                        <UnitProgramDropdown
                                            actingBodyId={getActingBodyId()}
                                            value={unitProgramId}
                                            setValue={setUnitProgramId}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? {} : { maxWidth: '30%' }]}
                                            required
                                            disabled={(isReadingMode || (IsEditing && !isScheduled))}
                                        />
                                    </SectionHeader>

                                    <SectionHeader viewZIndex={6} title='vacanciesOffered'>
                                        <FormInput
                                            value={quantity.toString()}
                                            onChangeText={(val) => setQuantity(Number(val))}
                                            required
                                            label={translate('quantity')}
                                            mode='NUMBER'
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={[{ gap: 8 }, windowInfo.isMobile ? {} : { maxWidth: '20%' }]}
                                            disabled={(isReadingMode || (IsEditing && !isScheduled))}
                                        />
                                    </SectionHeader>

                                    <SectionHeader
                                        viewZIndex={5}
                                        title='vacancyLevel'
                                        required
                                        endContent={
                                            <Tooltip
                                                useSimpleBox
                                                style={{
                                                    color: theme.colors.white['500'],
                                                    padding: 16,
                                                    borderRadius: 12,
                                                    justifyContent: 'center',
                                                    zIndex: 9999,
                                                    background: theme.colors.neutral['500'],
                                                }}
                                                tooltipContent={translate('levelRegisteredWithoutRoleAssociated')}
                                                tooltipDirection='right'
                                            >
                                                <FontAwesomeIcon icon={faCircleInfo} fontSize={14} color={theme.colors.neutral['500']} />
                                            </Tooltip>
                                        }
                                    >
                                        <VacancyLevelsInputs
                                            actingBodyId={getActingBodyId()}
                                            value={levelsIds}
                                            setValue={setLevelsIds}
                                            disabled={(isReadingMode || (IsEditing && !isScheduled))}
                                        />
                                    </SectionHeader>

                                    <SectionHeader viewZIndex={applicationsEstimatedStartZIndex - 1} childrenZIndex={applicationsEstimatedStartZIndex} title='vacancyOpening'>
                                        <View style={[{ zIndex: applicationsEstimatedStartZIndex }, windowInfo.isMobile ? {} : { maxWidth: '25%' }]}>
                                            <FilterDatePicker
                                                date={applicationsStartInDateFilter}
                                                onChangeDate={setApplicationsStartInDateFilter}
                                                time={applicationsStartInHourFilter}
                                                onChangeTime={setApplicationsStartInHourFilter}
                                                label={translate('openingDateHour')}
                                                onCalendarOpen={() => setApplicationsEstimatedStartZIndex(99999)}
                                                onCalendarClose={() => setApplicationsEstimatedStartZIndex(4)}
                                                required
                                                disabled={(isReadingMode || (IsEditing && !isScheduled))}
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={{ gap: 8, flex: 1 }}
                                            />
                                        </View>
                                    </SectionHeader>

                                    <SectionHeader viewZIndex={applicationsEstimatedFinishZIndex - 1} childrenZIndex={applicationsEstimatedFinishZIndex} title='applications'>
                                        <View style={[{ zIndex: applicationsEstimatedFinishZIndex }, windowInfo.isMobile ? {} : { maxWidth: '25%' }]}>
                                            <FilterDatePicker
                                                date={applicationsFinishInDateFilter}
                                                onChangeDate={setApplicationsFinishInDateFilter}
                                                time={applicationsFinishInHourFilter}
                                                onChangeTime={setApplicationsFinishInHourFilter}
                                                label={translate('finalDateHour')}
                                                onCalendarOpen={() => seApplicationsEstimatedFinishZIndex(99999)}
                                                onCalendarClose={() => seApplicationsEstimatedFinishZIndex(3)}
                                                disabled={(isReadingMode || (IsEditing && vacancy?.applicationsFinish !== null))}
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={{ gap: 8, flex: 1 }}
                                            />
                                        </View>
                                        {!applicationsFinishLaterThanStart()
                                            ? <MyAppText style={styles.errorMessage}>{translate('applicationsFinishMustBeAfterApplicationsStart')}</MyAppText>
                                            : null
                                        }
                                    </SectionHeader>

                                    <SectionHeader viewZIndex={5} childrenZIndex={6} title='activityCreation'>
                                        <View style={{ flex: 1, zIndex: 2, flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 16 }}>
                                            <View style={[{ zIndex: estimatedStartZIndex }, windowInfo.isMobile ? {} : { maxWidth: '25%', flex: 1 }]}>
                                                <FilterDatePicker
                                                    date={activityStartInDateFilter}
                                                    onChangeDate={setActivityStartInDateFilter}
                                                    time={activityStartInHourFilter}
                                                    onChangeTime={setActivityStartInHourFilter}
                                                    label={translate('startTime')}
                                                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                                                    onCalendarClose={() => setEstimatedStartZIndex(2)}
                                                    required
                                                    disabled={(isReadingMode || (IsEditing && !isScheduled))}
                                                    labelStyle={styles.fieldsLabelStyle}
                                                    viewStyle={{ gap: 8 }}
                                                />
                                            </View>
                                            <View style={[{ zIndex: estimatedFinishZIndex }, windowInfo.isMobile ? {} : { maxWidth: '25%', flex: 1 }]}>
                                                <FilterDatePicker
                                                    date={activityFinishInDateFilter}
                                                    onChangeDate={setActivityFinishInDateFilter}
                                                    time={activityFinishInHourFilter}
                                                    onChangeTime={setActivityFinishInHourFilter}
                                                    label={translate('endTime')}
                                                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                                                    onCalendarClose={() => setEstimatedFinishZIndex(1)}
                                                    required
                                                    disabled={(isReadingMode || (IsEditing && !isScheduled))}
                                                    labelStyle={styles.fieldsLabelStyle}
                                                    viewStyle={{ gap: 8, flex: 1 }}
                                                />
                                            </View>
                                        </View>
                                        {!activityStartLaterThanApplicationsFinish()
                                            ? <MyAppText style={styles.errorMessage}>{translate('activityStartMustBeAfterApplicationsFinish')}</MyAppText>
                                            : !activityStartLaterThanApplicationsStart()
                                                ? <MyAppText style={styles.errorMessage}>{translate('activityStartMustBeAfterApplicationsStart')}</MyAppText>
                                                : null
                                        }

                                        {!activityFinishLaterThanActivityStart()
                                            ? <MyAppText style={styles.errorMessage}>{translate('activityFinishMustBeAfterActivityFinish')}</MyAppText>
                                            : null
                                        }
                                    </SectionHeader>

                                    <SectionHeader viewZIndex={1} title='requirements' required>
                                        <VacancyRequirementsInputs
                                            actingBodyId={getActingBodyId()}
                                            value={requirementsIds}
                                            setValue={setRequirementsIds}
                                            disabled={(isReadingMode || (IsEditing && !isScheduled))}
                                        />
                                    </SectionHeader>

                                    <SectionHeader title='tags'>
                                        <VacancyTagsInputs
                                            value={tagsIds}
                                            setValue={setTagsIds}
                                            disabled={isReadingMode}
                                        />
                                    </SectionHeader>
                                </ScrollView>;
                            } else if (selectedTab == 'applications') {
                                return IsEditing
                                    ? vacancy && <VacancyApplications vacancyId={vacancy.id} setIsCancellingApplications={setIsCancellingApplications} />
                                    : <View>
                                        <MyAppText style={{ fontSize: 14, fontWeight: '400', color: theme.colors.neutral['500'] }}>{translate('necessaryRegisterVacancyToAcceptApplications')}</MyAppText>
                                    </View>;
                            }
                        }}
                    </Tabs>
                }
            </Card>
            {vacancy && <CancelApplicationsModal
                isModalVisible={closeApplicationsConfirmModal}
                setIsModalVisible={setCloseApplicationsConfirmModal}
                onConfirm={() => {
                    setCloseApplicationsConfirmModal(false);
                    closeApplications();
                }}
                selectedVacancies={[vacancy]}
            />
            }
            {vacancy && <ExportAgentsSchedulesSelectModal
                isModalVisible={exportScheduleModal}
                setIsModalVisible={setExportScheduleModal}
                onConfirm={() => {
                    setExportScheduleModal(false);
                    if (scheduleFileFormat != '') {
                        getSchedule(scheduleFileFormat);
                    }
                    setScheduleFileFormat('');
                }}
                fileExtension={scheduleFileFormat}
                setFileExtension={setScheduleFileFormat}
            />}
            {vacancy && <DeleteVacancyModal
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
                vacancy={vacancy}
                onConfirm={() => navigation.navigate('Deac', { screen: 'Vacancies' })}
            />}
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    sectionHeaderTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    errorMessage: {
        color: theme.colors.errors['300'],
        fontWeight: '500',
        fontSize: 12,
    }
}));
